import { useState, useCallback, useRef } from "react";
import { useAppSelector } from "@/state/hooks";
import axios from "axios";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

// Minimum time (in ms) to show each processing phase to ensure UI visibility
const MIN_PHASE_DISPLAY_TIME = 500;

export interface FileUploadProps {
    /** Current product/project id (KAN-1584 follow-up). When set, passed to
     *  /api/vector_files/save_to_database so the backend auto-scopes the
     *  uploaded file to this product (ai.vector_files_project_cr). Omit or
     *  leave undefined when there's no current product (e.g. brand-new
     *  product not yet created) — the file saves unscoped, same as today. */
    projectId?: number;
    onUploadStart?: () => void;
    onUploadProgress?: (progress: number) => void;
    onUploadComplete?: (fileName: string | undefined) => void;
    onSavingFileToDatabaseStart?: (fileName: string | undefined) => void;
    onSavingFileToDatabaseComplete?: (fileName: string | undefined, fileId: number) => void;
    onFileTextExtractionStart?: (fileName: string) => void;
    onFileTextExtractionProgress?: (fileName: string, status: string, progress: number) => void;
    /** Raw parsed SSE event from /extract_text — additive; fires once per event,
     *  before onFileTextExtractionProgress. Gives callers the machine-readable
     *  status and chunk counts the message string embeds. */
    onFileTextExtractionEvent?: (fileName: string, event: FileExtractionEvent) => void;
    onFileTextExtractionComplete?: (fileName: string) => void;
    onError?: (error: string) => void;
    runVectorizer?: boolean;
}

export interface FileExtractionEvent {
    status: string;
    message: string;
    progress: number;
    current_chunk?: number;
    total_chunks?: number;
    fileid?: number;
}

export type UploadPhase = "idle" | "uploading" | "extracting" | "saving" | "complete" | "error";

export interface UseFileUploadReturn {
    // Main functions
    handleUpload: (file: File, options?: { signal?: AbortSignal }) => Promise<number | null>;
    beginUpload: (fileOrName: string | File) => Promise<number | null>;
    deleteFile: (fileId: number) => Promise<{ success: boolean; message?: string; error?: string }>;
    downloadFile: (fileId: number, fileName: string) => Promise<{ success: boolean; error?: string }>;

    // Current phase
    phase: UploadPhase;

    // State flags
    isUploading: boolean;
    isExtracting: boolean;
    isSaving: boolean;
    isComplete: boolean;

    // Progress and status
    uploadProgress: number;
    currentFileName: string | null;
    error: string | null;
    lastFileId: number | null;

    // Reset function
    reset: () => void;
}

const useFileUpload = (props: FileUploadProps): UseFileUploadReturn => {
    // Phase tracking
    const [phase, setPhase] = useState<UploadPhase>("idle");

    // State flags
    const [isUploading, setIsUploading] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    // Progress and status
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileName, setCurrentFileName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastFileId, setLastFileId] = useState<number | null>(null);

    // Track files being processed to prevent duplicates (using ref to avoid stale closure issues)
    const processingFilesRef = useRef<Set<string>>(new Set());

    const token = useAppSelector(state => state.clientToken.token);

    const reset = useCallback(() => {
        setPhase("idle");
        setIsUploading(false);
        setIsExtracting(false);
        setIsSaving(false);
        setIsComplete(false);
        setUploadProgress(0);
        setCurrentFileName(null);
        setError(null);
        setLastFileId(null);
        processingFilesRef.current = new Set(); // Clear processing files
    }, []);

    // Helper to ensure minimum display time for each phase
    const waitForMinDisplayTime = useCallback((startTime: number): Promise<void> => {
        const elapsed = Date.now() - startTime;
        const remaining = MIN_PHASE_DISPLAY_TIME - elapsed;
        if (remaining > 0) {
            return new Promise(resolve => setTimeout(resolve, remaining));
        }
        return Promise.resolve();
    }, []);

    const getApiHeader = useCallback(async () => {
        return { Authorization: `Bearer ${token}` };
    }, []);

    // Save file metadata to database and get fileId
    const saveToDatabase = useCallback(
        async (fileName: string, signal?: AbortSignal): Promise<number> => {
            const savePhaseStart = Date.now();
            setPhase("saving");
            setIsSaving(true);
            props.onSavingFileToDatabaseStart?.(fileName);
            console.log("[useFileUpload] Saving file to database:", fileName);

            try {
                const headers = await getApiHeader();
                const response = await axios.post(
                    `${BACKEND_URI}/api/vector_files/save_to_database`,
                    {
                        filename: fileName,
                        text: "", // Empty text since we're just creating the record
                        libraryContent: false,
                        ...(props.projectId != null ? { project_id: props.projectId } : {})
                    },
                    {
                        headers: {
                            ...headers,
                            "Content-Type": "application/json"
                        },
                        signal
                    }
                );

                const fileId = response.data.fileid;
                console.log(`[useFileUpload] File saved to database with fileId: ${fileId}`);

                setLastFileId(fileId);

                // Ensure saving phase is visible for minimum time
                await waitForMinDisplayTime(savePhaseStart);
                setIsSaving(false);
                props.onSavingFileToDatabaseComplete?.(fileName, fileId);

                return fileId;
            } catch (error) {
                console.error(`Failed to save file to database: ${fileName}`, error);
                setIsSaving(false);
                props.onError?.(`Failed to save file to database: ${fileName}`);
                throw error;
            }
        },
        [props, getApiHeader, waitForMinDisplayTime]
    );

    const convertFile = useCallback(
        async (file: File, fileId: number, signal?: AbortSignal) => {
            // Check if file is already being processed
            if (processingFilesRef.current.has(file.name)) {
                console.log("[useFileUpload] File already being processed, skipping:", file.name);
                return;
            }

            // Add to processing set
            processingFilesRef.current.add(file.name);

            const extractPhaseStart = Date.now();
            setPhase("extracting");
            setIsExtracting(true);
            props.onFileTextExtractionStart?.(file.name);
            console.log("[useFileUpload] Starting text extraction for:", file.name, "fileId:", fileId, "at", new Date().toISOString());

            try {
                const headers = await getApiHeader();
                console.log("[useFileUpload] Calling /api/vector_files/extract_text for fileId:", fileId);
                const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ""}/api/vector_files/extract_text`, {
                    method: "POST",
                    headers: {
                        ...headers,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ fileid: fileId }),
                    signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                const decoder = new TextDecoder();

                if (reader) {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value);
                        const lines = chunk.split('\n');

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                const data = line.slice(6);
                                
                                if (data === '[DONE]') {
                                    console.log('File extraction complete!');
                                    break;
                                }

                                try {
                                    const status = JSON.parse(data);
                                    console.log(`Extraction status: ${status.status} - ${status.message} (${status.progress}%)`);

                                    props.onFileTextExtractionEvent?.(file.name, status as FileExtractionEvent);

                                    // Call progress callback if provided
                                    props.onFileTextExtractionProgress?.(
                                        file.name, 
                                        status.message, 
                                        status.progress
                                    );
                                    
                                    if (status.status === 'error') {
                                        throw new Error(status.message);
                                    }
                                } catch (e) {
                                    if (e instanceof SyntaxError) {
                                        console.error('Error parsing status:', e);
                                    } else {
                                        throw e;
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error(`File conversion failed for ${file.name}`, error);
                props.onError?.(`File conversion failed for ${file.name}`);
                throw error;
            } finally {
                // Ensure extracting phase is visible for minimum time
                await waitForMinDisplayTime(extractPhaseStart);
                setIsExtracting(false);
                // Remove from processing set
                processingFilesRef.current.delete(file.name);
            }

            props.onFileTextExtractionComplete?.(file.name);
        },
        [props, getApiHeader, waitForMinDisplayTime]
    );

    // Note: saveToDatabase functionality has been moved to the backend extract_text endpoint
    // The backend now handles saving to both relational and vector databases during extraction
    // This prevents duplicate saves and ensures data consistency

    // Upload small files normally
    const uploadSingleFile = useCallback(
        async (file: File, signal?: AbortSignal) => {
            const formData = new FormData();
            formData.append("file", file);

            console.log("Uploading", file.name);

            try {
                await axios.post(`${BACKEND_URI}/api/vector_files/upload`, formData, {
                    headers: await getApiHeader(),
                    timeout: 600000, // Increase timeout to 10 min
                    signal,
                    onUploadProgress: progressEvent => {
                        if (progressEvent.total) {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            setUploadProgress(percent);
                            props.onUploadProgress?.(percent);
                        }
                    }
                });
            } catch (error) {
                console.error(`Upload failed for ${file.name}`, error);
                setUploadProgress(0);
                props.onUploadProgress?.(0);
                props.onError?.(`Upload failed for ${file.name}`);
                throw error;
            }

            setUploadProgress(100);
            props.onUploadProgress?.(100);
            props.onUploadComplete?.(file.name);
            console.log(`Upload successful for ${file.name}`);
        },
        [props, getApiHeader]
    );

    // Upload large files in chunks
    const uploadFileInChunks = useCallback(
        async (file: File, signal?: AbortSignal) => {
            const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = chunkIndex * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end); // Extract chunk

                const formData = new FormData();
                formData.append("file", chunk);
                formData.append("filename", file.name);
                formData.append("chunkIndex", String(chunkIndex));
                formData.append("totalChunks", String(totalChunks));
                const overallProgress = Math.round(((chunkIndex + 1) * 100) / totalChunks);
                setUploadProgress(overallProgress);
                props.onUploadProgress?.(overallProgress);

                try {
                    await axios.post(`${BACKEND_URI}/api/vector_files/upload_chunk`, formData, {
                        headers: await getApiHeader(),
                        timeout: 600000, // Increase timeout
                        signal,
                        onUploadProgress: progressEvent => {
                            if (progressEvent.total) {
                                const chunkPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                const totalPercent = Math.round((chunkIndex * 100 + chunkPercent) / totalChunks);
                                setUploadProgress(totalPercent);
                                props.onUploadProgress?.(totalPercent);
                            }
                        }
                    });
                } catch (error) {
                    setUploadProgress(0);
                    props.onUploadProgress?.(0);
                    props.onError?.(`Chunk upload failed for ${file.name}: ${error}`);
                    console.error(`Chunk ${chunkIndex} upload failed for ${file.name}`, error);
                    throw error; // Re-throw to be caught by handleUpload
                }
            }
            setUploadProgress(100);
            props.onUploadProgress?.(100);
            props.onUploadComplete?.(file.name);
        },
        [props, getApiHeader]
    );

    const handleUpload = useCallback(
        async (file: File, options?: { signal?: AbortSignal }): Promise<number | null> => {
            if (!file) return null;
            const signal = options?.signal;

            // Reset states
            reset();

            // Set initial states
            setPhase("uploading");
            setIsUploading(true);
            setCurrentFileName(file.name);

            props.onUploadStart?.();
            props.onUploadProgress?.(0);

            try {
                // Upload phase - upload to blob storage
                if (file.size > 10 * 1024 * 1024) {
                    await uploadFileInChunks(file, signal);
                } else {
                    await uploadSingleFile(file, signal);
                }
                setIsUploading(false);

                // Save to database and get fileId
                const fileId = await saveToDatabase(file.name, signal);

                // Extraction phase - pass fileId to extract_text
                await convertFile(file, fileId, signal);

                // Complete
                setPhase("complete");
                setIsComplete(true);
                console.log(`File upload complete with fileId: ${fileId}`);

                return fileId;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Upload failed";
                setPhase("error");
                setError(errorMessage);
                props.onError?.(errorMessage);

                // Reset other states on error
                setIsUploading(false);
                setIsExtracting(false);
                setIsSaving(false);

                return null;
            }
        },
        [props, uploadFileInChunks, uploadSingleFile, saveToDatabase, convertFile, reset]
    );

    // Begin upload with either a filename or File object
    const beginUpload = useCallback(
        async (fileOrName: string | File): Promise<number | null> => {
            if (!fileOrName) return null;

            // Reset states
            reset();

            // Determine if we have a File object or just a filename
            const isFile = fileOrName instanceof File;
            const fileName = isFile ? fileOrName.name : fileOrName;
            const file = isFile ? fileOrName : new File([], fileName);

            // Set initial states - always start with uploading phase for visual feedback
            setPhase("uploading");
            setIsUploading(true);
            setCurrentFileName(fileName);

            props.onUploadStart?.();

            try {
                const uploadPhaseStart = Date.now();

                // If we have a File object, upload it first
                if (isFile) {
                    props.onUploadProgress?.(0);

                    // Upload the file to blob storage
                    if (file.size > 10 * 1024 * 1024) {
                        await uploadFileInChunks(file);
                    } else {
                        await uploadSingleFile(file);
                    }
                } else {
                    // If just filename, simulate progress for visual feedback
                    props.onUploadProgress?.(100);
                    props.onUploadComplete?.(fileName);
                }

                // Ensure upload phase is visible for minimum time
                await waitForMinDisplayTime(uploadPhaseStart);
                setIsUploading(false);

                // Save to database and get fileId
                const fileId = await saveToDatabase(fileName);

                // Extraction phase - pass fileId to extract_text
                await convertFile(file, fileId);

                // Complete
                setPhase("complete");
                setIsComplete(true);
                console.log(`File processing complete with fileId: ${fileId}`);

                return fileId;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Processing failed";
                setPhase("error");
                setError(errorMessage);
                props.onError?.(errorMessage);

                // Reset other states on error
                setIsUploading(false);
                setIsExtracting(false);
                setIsSaving(false);

                return null;
            }
        },
        [props, uploadFileInChunks, uploadSingleFile, saveToDatabase, convertFile, reset, waitForMinDisplayTime]
    );

    // Delete file function
    const deleteFile = useCallback(
        async (fileId: number): Promise<{ success: boolean; message?: string; error?: string }> => {
            try {
                const headers = await getApiHeader();
                const response = await axios.delete(
                    `${BACKEND_URI}/api/vector_files/delete/${fileId}`,
                    { headers }
                );

                if (response.status === 200) {
                    return {
                        success: true,
                        message: response.data.message
                    };
                } else {
                    return {
                        success: false,
                        error: response.data.error || "Failed to delete file"
                    };
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    return {
                        success: false,
                        error: error.response?.data?.error || error.message || "Failed to delete file"
                    };
                }
                return {
                    success: false,
                    error: "An unexpected error occurred while deleting the file"
                };
            }
        },
        [getApiHeader]
    );

    // Download file function
    const downloadFile = useCallback(
        async (fileId: number, fileName: string): Promise<{ success: boolean; error?: string }> => {
            try {
                const headers = await getApiHeader();
                const response = await axios.get(
                    `${BACKEND_URI}/api/vector_files/download/${fileId}`,
                    {
                        headers,
                        responseType: 'blob' // Important for file downloads
                    }
                );

                if (response.status === 200) {
                    // Create a blob URL and trigger download
                    const blob = new Blob([response.data], { type: response.headers['content-type'] });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    return {
                        success: true
                    };
                } else {
                    return {
                        success: false,
                        error: "Failed to download file"
                    };
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    // For blob responses, we need to read the error from the blob
                    if (error.response?.data instanceof Blob) {
                        try {
                            const errorText = await error.response.data.text();
                            const errorData = JSON.parse(errorText);
                            return {
                                success: false,
                                error: errorData.error || "Failed to download file"
                            };
                        } catch {
                            return {
                                success: false,
                                error: error.message || "Failed to download file"
                            };
                        }
                    }
                    return {
                        success: false,
                        error: error.response?.data?.error || error.message || "Failed to download file"
                    };
                }
                return {
                    success: false,
                    error: "An unexpected error occurred while downloading the file"
                };
            }
        },
        [getApiHeader]
    );

    return {
        handleUpload,
        beginUpload,
        deleteFile,
        downloadFile,
        phase,
        isUploading,
        isExtracting,
        isSaving,
        isComplete,
        uploadProgress,
        currentFileName,
        error,
        lastFileId,
        reset
    };
};

export default useFileUpload;
